import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { JobPositionsService } from "../../job-positions.service";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import * as moment from "moment";
import { CitiesService } from "../../../../auth/service/cities.service";
import { UtilsService } from "../../../../auth/service/utils.service";
import { JobSiteService } from "../../../../auth/service/job-sites.service";
import { UserFigureService } from "../../../../auth/service/user-figure.service";
import { RequestService } from "../../../recruitment/requests/request.service";
import { AuthenticationService } from "../../../../auth/service";
import { IContentHeader } from "app/layout/components/content-header/content-header.component";
import { IFigure } from "app/auth/models/figures";
import { IJobCategory, IJobPosition, IJobPositionForm } from "app/auth/models/jobs";

@Component({
  selector: "app-form-job-positions",
  templateUrl: "./form-job-positions.component.html",
  styleUrls: ["./form-job-positions.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class FormJobPositionsComponent implements OnInit {
  public contentHeader: IContentHeader;
  public formGroup: FormGroup;
  public isEdit: boolean = false;
  public currentSlug: string = "";
  public figures: IFigure[] = [];
  public requests: any = [];
  public jobCategories: IJobCategory[] = [];
  public locations: { id: number; name: string }[] = [];
  public showErrors: boolean = false;
  public activeTab: string = "detail";
  public isSubmittingForm: boolean = false;
  public editJobPositionId: number = null;
  public jobPosition: IJobPosition | null = null

  constructor(
    private _formBuilder: FormBuilder,
    private _jobPositionService: JobPositionsService,
    private _jobSiteService: JobSiteService,
    private _figureService: UserFigureService,
    public _citiesService: CitiesService,
    private route: ActivatedRoute,
    private _router: Router,
    public _utilService: UtilsService,
    private _requestService: RequestService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _authenticationService: AuthenticationService,
  ) {
    this._utilService.disableAutoHideLoading();
    this.route.params.subscribe(async (params) => {
      if (params.hasOwnProperty("slug")) {
        this.isEdit = true;
        this.currentSlug = params.slug;
      }
    });
    this.formGroup = this._formBuilder.group({
      title: new FormControl('', [Validators.required]),
      figure_id: new FormControl({ value: null }, [Validators.required]),
      description: new FormControl(null, [Validators.required, Validators.minLength(10)]),
      summary: new FormControl(null, [Validators.required, Validators.minLength(10)]),
      start_date: new FormControl(moment().format("YYYY-MM-DD"), [Validators.required, Validators.pattern("^\\d{4}\\-(0?[1-9]|1[012])\\-(0?[1-9]|[12][0-9]|3[01])$")]),
      city_id: new FormControl({ value: null }, [Validators.required]),
      site_id: new FormControl({ value: null }, [Validators.required, Validators.min(0)]),
      category_id: new FormControl(null, [Validators.required, Validators.min(0)]),
      request_id: new FormControl({ value: null }),
    });
  }

  async ngOnInit() {
    if (this.isEdit) {
      if (!this._authenticationService.hasCapability("edit_job_position")) {
        await this._router.navigateByUrl("/dashboard/main");
        return false;
      }
      const jobPosition: IJobPosition = await this._jobPositionService.getJobPosition(this.currentSlug);
      this.jobPosition = jobPosition
      if (!jobPosition) {
        await this._router.navigateByUrl("/dashboard/main");
        await this._utilService.showToastrError("ERROR_NOT_FOUND_JOB_POSITION");
        return false;
      }
      if (!!jobPosition) {
        this.formGroup.patchValue({
          title: jobPosition.title,
          figure_id: jobPosition.figure_id,
          description: jobPosition.description,
          summary: jobPosition.summary,
          start_date: moment(jobPosition.start_date).format("YYYY-MM-DD"),
          city_id: jobPosition.city_id,
          site_id: jobPosition.site_id,
          category_id: jobPosition.category_id,
          request_id: jobPosition.request_id,
        });
        this.editJobPositionId = jobPosition.id;
      }
    } else {
      if (!this._authenticationService.hasCapability("create_job_position")) {
        await this._router.navigateByUrl("/dashboard/main");
        return false;
      }
      this.formGroup.patchValue({
        description: `${this._utilService.themeConfiguration.description_text.opening_description}<div><br></div> ${this._utilService.themeConfiguration.description_text.closure_description}`,
      });
    }
    this.contentHeader = {
      headerTitle: this.isEdit ? "EDIT_JOB_POSITION" : "ADD_JOB_POSITION",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/",
          },
          {
            name: "TITLES.JOB_POSITIONS",
            isLink: true,
            link: "/dashboard/job-positions",
          },
          {
            name: this.isEdit ? "EDIT" : "ADD",
            isLink: false,
          },
        ],
      },
    };

    this.jobCategories = await this._jobPositionService.getListJobCategories();
    this.locations = await this._jobSiteService.getList();
    this.figures = await this._figureService.getList();
    this.requests = await this._requestService.getRequestList();
    this._utilService.hideLoading();
  }

  changeTab(tab: string) {
    this.activeTab = this.activeTab === tab ? "" : tab;
    this._changeDetectorRef.detectChanges();
  }

  fieldHasErrors(field_name: string): boolean {
    if (this.showErrors) {
      return this.formGroup.controls[field_name].status === "INVALID";
    } else {
      return this.formGroup.controls[field_name].status === "INVALID" && this.formGroup.controls[field_name].touched;
    }
  }

  async manageRequest() {
    if (!!this.formGroup.value.request_id) {
      this._utilService.showLoading()
      const request: any = await this._requestService.getRequest(this.formGroup.value.request_id);
      const descriptionObj: { description: string; summary: string } = await this._jobPositionService.getDescriptionFromRequest(this.formGroup.value.request_id);
      this.formGroup.patchValue({
        city_id: request.city_id,
        figure_id: request.figure_id,
        site_id: request.site_id,
        description: descriptionObj.description,
        summary: descriptionObj.summary,
      });
      this._utilService.hideLoading()
    } else {
      this.formGroup.patchValue({
        city_id: null,
        figure_id: null,
        site_id: null,
        summary: null,
      });
    }
  }

  async submitCreate() {
    if (!this._authenticationService.hasCapability("create_job_position")) {
      await this._router.navigateByUrl("/dashboard/main");
      return false;
    }
    this.showErrors = true;
    if (!this.formGroup.value.figure_id || !this.formGroup.value.category_id || !this.formGroup.value.city_id || !this.formGroup.value.site_id) {
      this.activeTab = "detail";
      document.getElementById("detail-section").scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
      return false;
    }
    if (!this.formGroup.value.summary) {
      this.activeTab = "summary";
      document.getElementById("summary-section").scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
      return false;
    }
    if (!this.formGroup.value.description) {
      this.activeTab = "description";
      document.getElementById("description-section").scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
      return false;
    }
    this.isSubmittingForm = true;
    this.formGroup.disable();
    let data: IJobPositionForm = this.formGroup.value;
    await this._jobPositionService
      .createJobPosition(data)
      .then(() => {
        this.isSubmittingForm = false;
        this.formGroup.enable();
        this.showErrors = false;
        this._utilService.showToastrSuccess("SUCCESS_JOB_POSITION_INSERT");
        this._router.navigateByUrl("/dashboard/job-positions");
      })
      .catch(() => {
        this.isSubmittingForm = false;
        this.formGroup.enable();
        this._utilService.showToastrError("ERROR_JOB_POSITION_INSERT");
      });
  }

  async submitUpdate() {
    if (!this._authenticationService.hasCapability("edit_job_position")) {
      await this._router.navigateByUrl("/dashboard/main");
      return false;
    }
    this.showErrors = true;
    if (!this.formGroup.value.category_id) {
      this.activeTab = "detail";
      document.getElementById("detail-section").scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
      return false;
    }
    if (!this.formGroup.value.summary) {
      this.activeTab = "summary";
      document.getElementById("summary-section").scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
      return false;
    }
    if (!this.formGroup.value.description) {
      this.activeTab = "description";
      document.getElementById("description-section").scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
      return false;
    }
    this.isSubmittingForm = true;
    this.formGroup.disable();
    let data: IJobPositionForm = this.formGroup.value;

    await this._jobPositionService
      .updateJobPosition(this.editJobPositionId, data)
      .then(() => {
        this.isSubmittingForm = false;
        this.formGroup.enable();
        this.showErrors = false;
        this._utilService.showToastrSuccess("SUCCESS_JOB_POSITION_EDIT");
        this._router.navigateByUrl("/dashboard/job-positions");
      })
      .catch(() => {
        this.isSubmittingForm = false;
        this.formGroup.enable();
        this._utilService.showToastrError("ERROR_JOB_POSITION_EDIT");
      });
  }
}
